import React, { useEffect, useMemo, useState } from 'react';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { resolveImageEntries } from '../../utils/entityImages';
import { monsterKind } from '../../utils/monsterKind';
import { extraMonsterSheets, EXTRA_SHEET_SIZE, extraPhotoCount, extraRectInBounds } from '../../data/guide/monsterExtraPhotoSheets';
import type { WorldData } from '../../types/world';
type SheetState = {
  status: 'cargando' | 'ok' | 'error';
  w?: number;
  h?: number;
};

/** IDs que el staff pidió revisar expresamente. */
const CHECK_IDS = [313, 420, 315, 460, 461, 462, 407, 42];

/** Panel del staff: cifras reales del paquete VANGUARD_MONSTRUOS_EXTRA, calculadas en este navegador. */
export function ExtraPhotoPackCheck({
  w
}: {
  w: WorldData;
}) {
  const {
    extraPack,
    byMonster
  } = useMemo(() => resolveImageEntries(w), [w]);
  const [run, setRun] = useState(false);
  const [sheets, setSheets] = useState<Record<number, SheetState>>({});
  useEffect(() => {
    if (!run) return;
    Object.entries(extraMonsterSheets).forEach(([id, url]) => {
      const n = Number(id);
      setSheets(s => ({
        ...s,
        [n]: {
          status: 'cargando'
        }
      }));
      const img = new Image();
      img.onload = () => setSheets(s => ({
        ...s,
        [n]: {
          status: 'ok',
          w: img.naturalWidth,
          h: img.naturalHeight
        }
      }));
      img.onerror = () => setSheets(s => ({
        ...s,
        [n]: {
          status: 'error'
        }
      }));
      img.src = url;
    });
  }, [run]);
  const nuevas = extraPack.filter(r => r.status === 'nueva');
  const conservadas = extraPack.filter(r => r.status === 'conservada');
  const fuera = extraPack.filter(r => r.status === 'fuera de catálogo');
  const badRect = extraPack.filter(r => !extraRectInBounds(r.photo));
  const loaded = Object.values(sheets).filter(s => s.status === 'ok').length;
  const failed = Object.entries(sheets).filter(([, s]) => s.status === 'error').map(([k]) => Number(k));
  const visibles = run ? nuevas.filter(r => sheets[r.photo.sheet]?.status === 'ok' && extraRectInBounds(r.photo)).length : undefined;
  const all = Array.from(w.monsters.values());
  const sinFoto = all.filter(m => !byMonster.get(m.id)?.image);
  const sinFotoMon = sinFoto.filter(m => monsterKind(w, m) === 'monstruo');
  const sinFotoNpc = sinFoto.filter(m => monsterKind(w, m) === 'npc');
  const checks = CHECK_IDS.map(id => {
    const m = w.monsters.get(id);
    if (!m) return `${id}\tno existe en 06_monsters`;
    const img = byMonster.get(id)?.image;
    const origin = !img ? 'sin foto (marcador neutro)' : img.origin === 'staff' ? 'foto del staff (conservada)' : img.packName ?? 'VANGUARD_MOBS_LIMPIOS';
    return `${id}\t${m.name}\t${monsterKind(w, m).toUpperCase()}\t${origin}\tvisible en lista y buscador`;
  });
  const report = [`Catálogo 06_monsters cargado: ${all.length} registros (incluye NPC y objetos de evento). Todos tienen ficha y resultado, con o sin foto.`, `IDs del paquete EXTRA: ${extraPack.length} (${extraPhotoCount} fotos distintas) · en 06_monsters: ${extraPack.length - fuera.length} · fuera del catálogo: ${fuera.length}`, `Fotos nuevas por ID: ${nuevas.length} · conservadas (ya tenían foto aprobada): ${conservadas.length}${conservadas.length ? ` — ${conservadas.map(r => `${r.photo.id} ${r.catalogName}`).join(', ')}` : ''}`, `Láminas que cargan: ${run ? `${loaded}/3` : 'sin comprobar'} · recortes dentro de su celda: ${extraPack.length - badRect.length}/${extraPack.length} · fotos nuevas que se ven: ${visibles ?? 'sin comprobar'}`, `Sin foto (marcador neutro): ${sinFoto.length} — monstruos/objetos ${sinFotoMon.length} · NPC ${sinFotoNpc.length}`, '', 'COMPROBACIONES PEDIDAS (ID · nombre · clasificación · foto)', ...checks, '', ...fuera.map(r => `${r.photo.id}\t${r.photo.sourceName}\tno existe en 06_monsters: no se muestra`), ...badRect.map(r => `${r.photo.id}\t${r.photo.sourceName}\trecorte fuera de su celda`), ...failed.flatMap(s => extraPack.filter(r => r.photo.sheet === s).map(r => `${r.photo.id}\t${r.photo.sourceName}\tla lámina ${s} no cargó`)), ...Object.entries(sheets).filter(([, s]) => s.status === 'ok' && (s.w !== s.h || s.w !== EXTRA_SHEET_SIZE)).map(([k, s]) => `Lámina ${k}\t${s.w}×${s.h}\tno mide 1536 × 1536: el recorte se escala proporcionalmente`), '', `SIN FOTO (${sinFoto.length})`, ...sinFoto.sort((a, b) => a.id - b.id).map(m => `${m.id}\t${m.name}\t${monsterKind(w, m)}`)];
  return <section aria-labelledby="extra-pack" className="mt-8" data-id="element-1273" data-mp="2dwmao42ot5ou">
      <h3 id="extra-pack" className="text-lg font-semibold text-white" data-id="element-1274" data-mp="2dx446sxyt5t2">Paquete VANGUARD_MONSTRUOS_EXTRA</h3>
      <p className="mt-1 text-sm text-gray-400" data-id="element-1275" data-mp="28vol36fcl9db">Índice compacto no recibido: recortes medidos sobre las láminas; procedencia y referencia de familia del índice pendientes por ID.</p>
      <div className="mt-3 flex flex-wrap gap-2" data-id="element-1276" data-mp="xj5oryqdfkac">
        <button type="button" onClick={() => setRun(true)} className="h-9 rounded-xl border border-white/10 px-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1277" data-mp="sjj8fgf8xnxb">
          Comprobar carga de las láminas EXTRA
        </button>
        <CopyTextButton text={report.join('\n')} label="Copiar comprobación EXTRA" data-id="element-1278" data-mp="ncbduwi7oqyz" />
      </div>
      <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-white/[0.03] p-3 text-xs text-gray-300" data-id="element-1279" data-mp="sc7j0z2naog4">{report.join('\n')}</pre>
      <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-8" data-id="element-1280" data-mp="116c5756u3bpb">
        {CHECK_IDS.filter(id => w.monsters.has(id)).map(id => {
        const name = w.monsters.get(id)!.name;
        return <li key={id} className="text-center" data-id="element-1281" data-mp="1b51l4sf66m2x">
              <EntityImage entry={byMonster.get(id)} alt={name} name={name} size="card" kind="monstruo" data-id="element-1282" data-mp="1g335rhs8utgi" />
              <span className="mt-1 block truncate text-xs text-gray-400" data-id="element-1283" data-mp="3cvd60ni58x54">{id} · {name}</span>
            </li>;
      })}
      </ul>
    </section>;
}